import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Droplets, ArrowLeft } from 'lucide-react';

const PoliticaPrivacidad = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Política de Privacidad | Aqua Nexia</title>
        <meta name="description" content="Política de privacidad de Aqua Nexia - Cómo tratamos tus datos personales." />
      </Helmet>

      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">Aqua Nexia</span>
          </Link>
          <Link to="/" className="text-sm text-sky-600 hover:text-sky-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Política de Privacidad</h1>
        <p className="text-sm text-slate-500 mb-8">Última actualización: Febrero 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">1. Responsable del tratamiento</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Identidad:</strong> [Nombre de la empresa]</li>
              <li><strong>CIF/NIF:</strong> [Número de identificación fiscal]</li>
              <li><strong>Dirección:</strong> [Dirección completa]</li>
              <li><strong>Correo electrónico:</strong> privacidad@aquanexia.com</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">2. Datos personales que recopilamos</h2>
            <p>En función de la finalidad, tratamos las siguientes categorías de datos:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><strong>Datos de registro:</strong> nombre, dirección de correo electrónico, teléfono, nombre de empresa.</li>
              <li><strong>Datos de facturación:</strong> nombre fiscal, CIF/NIF, dirección fiscal, datos de pago procesados por Stripe.</li>
              <li><strong>Datos de uso:</strong> información sobre clientes, trabajadores, trabajos, reportes e inventario gestionados a través de la plataforma.</li>
              <li><strong>Datos técnicos:</strong> dirección IP, tipo de navegador, sistema operativo, cookies (ver Política de Cookies).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">3. Finalidad del tratamiento</h2>
            <p>Los datos personales se tratan con las siguientes finalidades:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li>Gestión de la relación contractual y prestación del servicio SaaS contratado.</li>
              <li>Procesamiento de pagos y facturación a través de Stripe.</li>
              <li>Envío de comunicaciones relacionadas con el servicio (notificaciones, alertas, cambios).</li>
              <li>Envío de comunicaciones comerciales, siempre con consentimiento previo.</li>
              <li>Mejora y optimización del servicio y la experiencia del usuario.</li>
              <li>Cumplimiento de obligaciones legales.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">4. Base legal del tratamiento</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Ejecución del contrato:</strong> la prestación del servicio SaaS contratado.</li>
              <li><strong>Consentimiento:</strong> para el envío de comunicaciones comerciales y el uso de cookies no esenciales.</li>
              <li><strong>Interés legítimo:</strong> mejora del servicio y prevención de fraude.</li>
              <li><strong>Obligación legal:</strong> cumplimiento de la normativa fiscal y contable.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">5. Encargados del tratamiento</h2>
            <p>Para la prestación del servicio, compartimos datos con los siguientes proveedores (encargados del tratamiento):</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><strong>Stripe:</strong> procesamiento de pagos (ubicación: UE/EE.UU. con cláusulas contractuales tipo).</li>
              <li><strong>MongoDB Atlas:</strong> almacenamiento de datos en la nube.</li>
              <li><strong>Resend:</strong> envío de correos electrónicos transaccionales.</li>
              <li><strong>OpenAI:</strong> funcionalidades de asistente IA (los datos se procesan de forma anónima).</li>
            </ul>
            <p>
              Todos los encargados del tratamiento ofrecen garantías suficientes para cumplir con el RGPD 
              y han suscrito los correspondientes contratos de encargo de tratamiento.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">6. Conservación de los datos</h2>
            <p>Los datos personales se conservarán durante los siguientes plazos:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><strong>Datos de cuenta:</strong> mientras la cuenta de usuario esté activa y hasta 30 días después de la cancelación.</li>
              <li><strong>Datos de facturación:</strong> 5 años según la normativa fiscal española.</li>
              <li><strong>Datos de uso de la plataforma:</strong> mientras dure la relación contractual.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">7. Derechos del usuario (RGPD)</h2>
            <p>De acuerdo con el Reglamento General de Protección de Datos (UE) 2016/679, el usuario puede ejercer los siguientes derechos:</p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><strong>Acceso:</strong> conocer si se tratan sus datos y obtener una copia.</li>
              <li><strong>Rectificación:</strong> solicitar la corrección de datos inexactos o incompletos.</li>
              <li><strong>Supresión:</strong> solicitar la eliminación de sus datos ("derecho al olvido").</li>
              <li><strong>Limitación:</strong> solicitar la limitación del tratamiento en determinadas circunstancias.</li>
              <li><strong>Portabilidad:</strong> recibir los datos en un formato estructurado y de uso común.</li>
              <li><strong>Oposición:</strong> oponerse al tratamiento de sus datos en determinados supuestos.</li>
            </ul>
            <p className="mt-3">
              Para ejercer estos derechos, puede enviar una solicitud a <strong>privacidad@aquanexia.com</strong> indicando 
              su nombre completo y el derecho que desea ejercer. Responderemos en un plazo máximo de 30 días.
            </p>
            <p>
              Si considera que el tratamiento no se ajusta a la normativa vigente, puede presentar una reclamación 
              ante la <strong>Agencia Española de Protección de Datos (AEPD)</strong> en <a href="https://www.aepd.es" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">www.aepd.es</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">8. Seguridad de los datos</h2>
            <p>
              Aqua Nexia aplica medidas técnicas y organizativas adecuadas para proteger los datos personales, 
              incluyendo cifrado de contraseñas, conexiones seguras (HTTPS/TLS), control de acceso basado en roles 
              y copias de seguridad periódicas.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 border-t mt-12">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
          <Link to="/aviso-legal" className="hover:text-sky-600">Aviso Legal</Link>
          <span>·</span>
          <Link to="/politica-privacidad" className="hover:text-sky-600">Privacidad</Link>
          <span>·</span>
          <Link to="/terminos-condiciones" className="hover:text-sky-600">Términos</Link>
          <span>·</span>
          <Link to="/politica-cookies" className="hover:text-sky-600">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaPrivacidad;
